import type { ContextHint, Question } from "./api";
import { failedExpectations, normalizeAgreeValues } from "./context-checks";

export interface ContextAnswerReview {
    key: string;
    questionId: string;
    questionText: string;
    label: string;
    action: "Select" | "Deselect";
    conflict: boolean;
    sources: ContextHint[];
}

/** Only actual choice controls can produce select/deselect suggestions. */
export function buildContextAnswerReview(
    questions: readonly Question[],
    answers: Record<string, string | undefined>,
    hints: readonly ContextHint[]
): ContextAnswerReview[] {
    const results: ContextAnswerReview[] = [];
    for (const q of questions) {
        if (
            q.visibleOnMobile === false ||
            q.isRetired ||
            (q.config as { hidden?: boolean } | null)?.hidden === true ||
            !["check", "radio", "dropDown", "yesNo"].includes(q.type)
        ) {
            continue;
        }
        const options =
            q.type === "yesNo"
                ? ["Yes", "No"].map((value) => ({ value, label: value }))
                : (q.answerOptions ?? []).map((o) => ({ value: o.value, label: o.text }));
        if (!options.length) {
            continue;
        }
        const question = { options };
        const reports = hints
            .filter((h) => String(h.questionId) === String(q.id) && h.answer !== null)
            .flatMap((hint) =>
                (hint.options ?? []).flatMap((option) => {
                    const value = normalizeAgreeValues(question, [option.value])[0];
                    return value ? [{ hint, option: { ...option, value } }] : [];
                })
            );
        const raw = answers[q.id];
        const answer =
            raw == null || raw.trim() === "" || raw.trim() === "null"
                ? q.type === "check"
                    ? "[]"
                    : ""
                : raw;
        // Compare each source separately: an agreeing source must not erase a
        // conflicting source. Never interpret unmentioned options as deselected.
        for (const hint of new Set(reports.map((r) => r.hint))) {
            const expected = reports.filter((r) => r.hint === hint).map((r) => r.option);
            for (const option of failedExpectations(answer, expected, question)) {
                const action = option.present ? "Select" : "Deselect";
                const key = JSON.stringify([String(q.id), option.value, action]);
                const existing = results.find((r) => r.key === key);
                if (existing) {
                    if (!existing.sources.includes(hint)) {
                        existing.sources.push(hint);
                    }
                    continue;
                }
                results.push({
                    key,
                    questionId: String(q.id),
                    questionText: q.text,
                    label: options.find((o) => o.value === option.value)?.label ?? option.label,
                    action,
                    conflict: reports.some(
                        (r) =>
                            r.option.value === option.value && r.option.present !== option.present
                    ),
                    sources: [hint]
                });
            }
        }
    }
    return results;
}

/** No patient text in storage; one small flag per suggestion, scoped to this nurse/visit/section. */
export function contextReviewStorageKey(scope: string, item: ContextAnswerReview): string {
    const encoded = Array.from(item.key)
        .map((c) => (c.codePointAt(0) ?? 0).toString(16).padStart(6, "0"))
        .join("");
    return `context-review-v1.${scope}.${encoded}`;
}
